import React from 'react';
import { Elements } from '@stripe/react-stripe-js';
import { loadStripe } from '@stripe/stripe-js';
import { 
  Box, 
  Typography, 
  Card, 
  CardContent, 
  Grid, 
  Button,
  Chip,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Divider,
  useTheme,
  alpha
} from '@mui/material';
import {
  Payment as PaymentIcon,
  Check as CheckIcon,
  Star as StarIcon,
  TrendingUp as TrendingUpIcon,
  Security as SecurityIcon,
  Support as SupportIcon,
  Speed as SpeedIcon
} from '@mui/icons-material';

const stripePromise = loadStripe('pk_test_51RkOUWEEQM9jaautJ8Ooe8wpEPcIy2uYCkp8QIIf0OXaUPAcdrtzSapmpeYSK374R1oyY94waqGLSsdUsRDXCjIu002SPvEZke');

function Payment() {
  const theme = useTheme();

  const plans = [
    {
      name: 'Gratuito',
      price: 'R$ 0',
      period: 'para sempre',
      description: 'Perfeito para explorar as funcionalidades básicas',
      features: [
        'Acesso aos filtros e mapa de decisões',
        'Até 5 mensagens no chatbot',
        '1 defesa administrativa por mês',
        'Suporte por email'
      ],
      buttonText: 'Plano Atual',
      buttonVariant: 'outlined',
      popular: false,
    },
    {
      name: 'Premium Mensal',
      price: 'R$ 49,90',
      period: 'por mês',
      description: 'Ideal para uso profissional regular',
      features: [
        'Todas as funcionalidades do plano gratuito',
        'Até 100 mensagens no chatbot por mês',
        '20 defesas administrativas por mês',
        'Suporte prioritário',
        'Análises avançadas',
        'Relatórios personalizados'
      ],
      buttonText: 'Assinar Agora',
      buttonVariant: 'contained',
      popular: true,
    },
    {
      name: 'Premium Anual',
      price: 'R$ 499,00',
      period: 'por ano',
      description: 'Melhor valor para uso intensivo',
      savings: 'Economize R$ 98,80',
      features: [
        'Todas as funcionalidades do plano mensal',
        'Até 100 mensagens no chatbot por mês',
        '20 defesas administrativas por mês',
        'Suporte prioritário 24/7',
        'Análises avançadas e relatórios',
        'Acesso antecipado a novas funcionalidades',
        '2 meses grátis'
      ],
      buttonText: 'Assinar Anual',
      buttonVariant: 'contained',
      popular: false,
    },
  ];

  const addOnFeatures = [
    {
      icon: <SpeedIcon />,
      title: 'Pacotes Adicionais',
      description: 'R$ 29,90 por 100 mensagens extras + 20 defesas administrativas'
    },
    {
      icon: <SecurityIcon />,
      title: 'Pagamentos Seguros',
      description: 'Processamento seguro via Stripe com criptografia de ponta'
    },
    {
      icon: <SupportIcon />,
      title: 'Suporte Especializado',
      description: 'Equipe técnica especializada para ajudar com questões jurídicas'
    }
  ];

  return (
    <Elements stripe={stripePromise}>
      <Box>
        {/* Header */}
        <Box sx={{ mb: 4, textAlign: 'center' }}>
          <Typography 
            variant="h3" 
            sx={{ 
              fontWeight: 700,
              mb: 1,
              background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.light} 100%)`,
              backgroundClip: 'text',
              textFillColor: 'transparent',
              WebkitBackgroundClip: 'text',
              WebkitTextFillColor: 'transparent',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 2,
            }}
          >
            <PaymentIcon sx={{ fontSize: 40, color: theme.palette.primary.main }} />
            Planos e Assinaturas
          </Typography>
          <Typography variant="h6" color="text.secondary" sx={{ mb: 2 }}>
            Escolha o plano ideal para suas necessidades jurídicas
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Acesso a ferramentas de IA para análise de decisões do CRSNSP
          </Typography>
        </Box>

        {/* Pricing Plans */}
        <Grid container spacing={3} sx={{ mb: 4 }}>
          {plans.map((plan, index) => (
            <Grid item xs={12} md={4} key={index}>
              <Card 
                sx={{ 
                  height: '100%',
                  position: 'relative',
                  border: plan.popular ? `2px solid ${theme.palette.primary.main}` : '1px solid',
                  borderColor: plan.popular ? theme.palette.primary.main : theme.palette.divider,
                  transform: plan.popular ? 'scale(1.05)' : 'scale(1)',
                  transition: 'all 0.3s ease',
                  '&:hover': {
                    transform: plan.popular ? 'scale(1.07)' : 'scale(1.02)',
                    boxShadow: theme.shadows[8],
                  },
                }}
              >
                {plan.popular && (
                  <Box
                    sx={{
                      position: 'absolute',
                      top: -10,
                      left: '50%',
                      transform: 'translateX(-50%)',
                      zIndex: 1,
                    }}
                  >
                    <Chip
                      icon={<StarIcon />}
                      label="Mais Popular"
                      color="primary"
                      sx={{
                        background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.light} 100%)`,
                      }}
                    />
                  </Box>
                )}
                
                <CardContent sx={{ p: 3, height: '100%', display: 'flex', flexDirection: 'column' }}>
                  <Box sx={{ textAlign: 'center', mb: 3 }}>
                    <Typography variant="h5" sx={{ fontWeight: 600, mb: 1 }}>
                      {plan.name}
                    </Typography>
                    <Box sx={{ display: 'flex', alignItems: 'baseline', justifyContent: 'center', mb: 1 }}>
                      <Typography variant="h3" sx={{ fontWeight: 700, color: theme.palette.primary.main }}>
                        {plan.price}
                      </Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ ml: 1 }}>
                        {plan.period}
                      </Typography>
                    </Box>
                    {plan.savings && (
                      <Chip
                        label={plan.savings}
                        color="success"
                        size="small"
                        sx={{ mb: 1 }}
                      />
                    )}
                    <Typography variant="body2" color="text.secondary">
                      {plan.description}
                    </Typography>
                  </Box>

                  <List sx={{ flexGrow: 1, py: 0 }}>
                    {plan.features.map((feature, featureIndex) => (
                      <ListItem key={featureIndex} disablePadding sx={{ py: 0.5 }}>
                        <ListItemIcon sx={{ minWidth: 32 }}>
                          <CheckIcon 
                            sx={{ 
                              fontSize: 20, 
                              color: plan.popular ? theme.palette.primary.main : theme.palette.success.main 
                            }} 
                          />
                        </ListItemIcon>
                        <ListItemText 
                          primary={feature}
                          primaryTypographyProps={{ variant: 'body2' }}
                        />
                      </ListItem>
                    ))}
                  </List>

                  <Button
                    variant={plan.buttonVariant}
                    size="large"
                    fullWidth
                    sx={{
                      mt: 2,
                      py: 1.5,
                      background: plan.buttonVariant === 'contained' 
                        ? `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.light} 100%)`
                        : 'transparent',
                      '&:hover': {
                        background: plan.buttonVariant === 'contained'
                          ? `linear-gradient(135deg, ${theme.palette.primary.dark} 0%, ${theme.palette.primary.main} 100%)`
                          : alpha(theme.palette.primary.main, 0.04),
                      },
                    }}
                  >
                    {plan.buttonText}
                  </Button>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>

        {/* Additional Features */}
        <Card sx={{ mb: 4 }}>
          <CardContent>
            <Typography variant="h5" sx={{ fontWeight: 600, mb: 3, textAlign: 'center' }}>
              Recursos Adicionais
            </Typography>
            <Grid container spacing={3}>
              {addOnFeatures.map((feature, index) => (
                <Grid item xs={12} md={4} key={index}>
                  <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 2 }}>
                    <Box
                      sx={{
                        p: 1.5,
                        borderRadius: 2,
                        backgroundColor: alpha(theme.palette.primary.main, 0.1),
                        color: theme.palette.primary.main,
                      }}
                    >
                      {feature.icon}
                    </Box>
                    <Box>
                      <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
                        {feature.title}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        {feature.description}
                      </Typography>
                    </Box>
                  </Box>
                </Grid>
              ))}
            </Grid>
          </CardContent>
        </Card>

        {/* FAQ or Contact Info */}
        <Card>
          <CardContent sx={{ textAlign: 'center' }}>
            <Typography variant="h6" sx={{ fontWeight: 600, mb: 2 }}>
              Precisa de Ajuda?
            </Typography>
            <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
              Nossa equipe está pronta para ajudar você a escolher o melhor plano
            </Typography>
            <Button
              variant="outlined"
              size="large"
              startIcon={<SupportIcon />}
            >
              Entrar em Contato
            </Button>
          </CardContent>
        </Card>
      </Box>
    </Elements>
  );
}

export default Payment;
